import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollToSection } from "@/lib/scrollToSection";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Zap,
  TrendingUp,
  Trophy,
  BarChart3,
  Users,
  Award,
  Globe,
  Medal,
  BookOpen,
  Home,
  Menu,
  Calendar
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface SectionItem {
  id: string;
  labelKey: keyof ReturnType<typeof useLanguage>["t"]["sidebar"];
  icon: React.ReactNode;
  isRoute?: boolean;
  href?: string;
}

const sectionsDef: SectionItem[] = [
  { id: "hero", labelKey: "home", icon: <Home className="w-5 h-5" /> },
  { id: "live-games", labelKey: "liveResults", icon: <Zap className="w-5 h-5" /> },
  { id: "tournaments", labelKey: "leagues", icon: <Trophy className="w-5 h-5" /> },
  { id: "betting-odds", labelKey: "bettingOdds", icon: <TrendingUp className="w-5 h-5" /> },
  { id: "stats", labelKey: "statistics", icon: <BarChart3 className="w-5 h-5" /> },
  { id: "players", labelKey: "playerStats", icon: <Users className="w-5 h-5" /> },
  { id: "playoffs", labelKey: "playoffBracket", icon: <Award className="w-5 h-5" /> },
  { id: "international-tournaments", labelKey: "international", icon: <Globe className="w-5 h-5" /> },
  { id: "schedule", labelKey: "scheduleWatch", icon: <Calendar className="w-5 h-5" />, isRoute: true, href: "/schedule" },
  { id: "olympics-2026", labelKey: "olympics2026", icon: <Medal className="w-5 h-5" />, isRoute: true, href: "/olympics-2026" },
  { id: "standings", labelKey: "leagueStandings", icon: <Medal className="w-5 h-5" /> },
  { id: "rulebook", labelKey: "hockeyRules", icon: <BookOpen className="w-5 h-5" /> },
];

const MobileSidebar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isHomePage = location.pathname === "/";

  const handleScrollToSection = (id: string) => {
    setOpen(false);
    if (isHomePage) {
      scrollToSection(id, { maxAttempts: 30, pollInterval: 80 });
    } else {
      navigate("/");
      setTimeout(() => {
        scrollToSection(id, { maxAttempts: 40, pollInterval: 100 });
      }, 100);
    }
  };

  return (
    <div className="lg:hidden">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            className="fixed left-4 z-[55] bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:scale-105 transition-transform"
            style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}
            aria-label="Open navigation menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72 bg-card/95 backdrop-blur-md p-0">
          <SheetHeader className="border-b border-border/50 p-4">
            <SheetTitle className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                <Trophy className="w-5 h-5 text-primary" />
              </div>
              <span className="font-display text-lg">{t.sidebar.quickNav}</span>
            </SheetTitle>
          </SheetHeader>
          
          <nav className="p-3 overflow-y-auto max-h-[calc(100vh-120px)]">
            <ul className="space-y-1">
              {sectionsDef.map((section) => (
                <li key={section.id}>
                  {section.isRoute ? (
                    <Link
                      to={section.href!}
                      onClick={() => setOpen(false)}
                      className={cn(
                        "w-full flex items-center gap-4 px-4 py-3 rounded-xl text-base transition-all duration-200",
                        "text-muted-foreground hover:bg-primary/10 hover:text-foreground active:scale-[0.98]"
                      )}
                    >
                      <span className="text-primary">{section.icon}</span>
                      <span>{t.sidebar[section.labelKey]}</span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleScrollToSection(section.id)}
                      className={cn(
                        "w-full flex items-center gap-4 px-4 py-3 rounded-xl text-base transition-all duration-200",
                        "text-muted-foreground hover:bg-primary/10 hover:text-foreground active:scale-[0.98]"
                      )}
                    >
                      <span className="text-primary">{section.icon}</span>
                      <span>{t.sidebar[section.labelKey]}</span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </nav>
          
          <div className="absolute bottom-0 left-0 right-0 border-t border-border/50 p-4 bg-card/95">
            <p className="text-xs text-muted-foreground text-center">
              {t.sidebar.tapToNavigate}
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MobileSidebar;
